import React from 'react';
import type { RefObject } from 'react';

import { useDialog } from '@react-aria/dialog';
import { FocusScope } from '@react-aria/focus';
import { OverlayContainer, useModal, useOverlay, usePreventScroll } from '@react-aria/overlays';
import { mergeProps } from '@react-aria/utils';

import useContainerClassname from './hooks/container-class';
import useUnderlayClassname from './hooks/underlay-class';

export const clickDialogStyles = Object.freeze({
  underlay: {
    background: '#495569cc',
    display: 'flex',
    justifyContent: 'center'
  },
  container: {
    outline: 0,
    minWidth: '400px'
  }
});

export interface IProps extends React.PropsWithChildren {
  centered?: boolean;
  isDismissable?: boolean;
  isOpen?: boolean;
  isSecure?: boolean;
  overlayClassName?: string;
  shouldCloseOnBlur?: boolean;
  underlayClassName?: string;

  onClose?: () => void;
}

const ClickDialogContainer: React.FC<IProps> = ({
  children,
  centered,
  isDismissable,
  isSecure,
  isOpen,
  overlayClassName: pOverlayClassName,
  shouldCloseOnBlur,
  underlayClassName: pUnderlayClassName,
  onClose
}) => {
  const dialogRef = React.useRef<HTMLDivElement>() as RefObject<HTMLDivElement>;

  const { dialogProps } = useDialog(
    {
      role: 'dialog'
    },
    dialogRef
  );

  const { overlayProps, underlayProps } = useOverlay(
    {
      isDismissable: isDismissable,
      isOpen: isOpen,
      shouldCloseOnBlur: shouldCloseOnBlur,
      onClose: onClose
    },
    dialogRef
  );

  const overlayClassName = useContainerClassname(pOverlayClassName);
  const underlayClassName = useUnderlayClassname(pUnderlayClassName, centered, isSecure);

  usePreventScroll();

  const { modalProps } = useModal();

  if (!isOpen) return null;

  return (
    <OverlayContainer>
      <div {...underlayProps} style={clickDialogStyles.underlay} className={underlayClassName}>
        <FocusScope contain={true} restoreFocus={true}>
          <div
            {...mergeProps(overlayProps, dialogProps, modalProps)}
            ref={dialogRef}
            className={overlayClassName}
            style={clickDialogStyles.container}
          >
            {children}
          </div>
        </FocusScope>
      </div>
    </OverlayContainer>
  );
};

ClickDialogContainer.defaultProps = {
  isDismissable: true
};

export default ClickDialogContainer;
